import { FC, memo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  Button,
  LinearProgress,
} from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { T_KEYS } from '../../constants';

interface ImportingModalProps {
  open: boolean;
  onGoToDashboard: () => void;
  onStayHere: () => void;
}

const ImportingModal: FC<ImportingModalProps> = memo(({
  open,
  onGoToDashboard,
  onStayHere,
}) => {
  const { t } = useTranslation();
  const isRtl = document.documentElement.dir === 'rtl';
  return (
    <Dialog
      open={open}
      onClose={onStayHere}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '24px',
          padding: '32px',
          maxWidth: '452px',
          direction: isRtl ? 'rtl' : 'ltr',
        },
      }}
      BackdropProps={{
        sx: {
          backgroundColor: 'rgba(255, 255, 255, 0.48)',
          backdropFilter: 'blur(2.6px)',
        },
      }}
    >
      <DialogContent sx={{ p: 0 }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '24px',
          }}
        >
          <Box
            sx={{
              position: 'relative',
              width: '90px',
              height: '90px',
            }}
          >
            <Box
              sx={{
                position: 'absolute',
                width: '90px',
                height: '90px',
                borderRadius: '50%',
                background: 'rgba(91, 47, 249, 0.25)',
                filter: 'blur(16px)',
              }}
            />
            <Box
              sx={{
                position: 'absolute',
                width: '90px',
                height: '90px',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #5B2FF9 0%, #8C73FF 100%)',
                boxShadow: '0px 4px 12px rgba(91, 47, 249, 0.25)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <AccessTimeIcon sx={{ fontSize: 45, color: 'white' }} />
            </Box>
          </Box>

          <Typography
            sx={{
              fontFamily: '"IBM Plex Sans Arabic", sans-serif',
              fontWeight: 500,
              fontSize: '20px',
              lineHeight: '28px',
              color: '#261B6A',
              textAlign: 'center',
            }}
          >
            {t(T_KEYS.IMPORTING_MODAL_TITLE)}
          </Typography>

          <Typography
            sx={{
              fontFamily: '"IBM Plex Sans Arabic", sans-serif',
              fontWeight: 400,
              fontSize: '16px',
              lineHeight: '24px',
              color: '#6B6B6B',
              textAlign: 'center',
            }}
          >
            {t(T_KEYS.IMPORTING_MODAL_DESC)}
          </Typography>

          <Box sx={{ width: '313px' }}>
            <LinearProgress
              sx={{
                height: '5px',
                borderRadius: '9999px',
                backgroundColor: '#EAE3FE',
                '& .MuiLinearProgress-bar': {
                  background: 'linear-gradient(90deg, #5B2FF9 0%, #8C73FF 100%)',
                  borderRadius: '9999px',
                },
              }}
            />
          </Box>

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: '12px',
              width: '100%',
            }}
          >
            <Button
              variant="contained"
              onClick={onGoToDashboard}
              sx={{
                backgroundColor: '#5B3FFF',
                color: 'white',
                borderRadius: '8px',
                padding: '16px',
                fontFamily: '"Tajawal", sans-serif',
                fontWeight: 700,
                fontSize: '16px',
                lineHeight: '24px',
                textTransform: 'none',
                '&:hover': {
                  backgroundColor: '#4A32CC',
                },
              }}
            >
              {t(T_KEYS.GO_TO_DASHBOARD)}
            </Button>
            <Button
              variant="contained"
              onClick={onStayHere}
              sx={{
                backgroundColor: '#F2F2F2',
                color: '#262626',
                borderRadius: '8px',
                padding: '16px',
                fontFamily: '"Tajawal", sans-serif',
                fontWeight: 700,
                fontSize: '16px',
                lineHeight: '24px',
                textTransform: 'none',
                '&:hover': {
                  backgroundColor: '#E5E5E5',
                },
              }}
            >
              {t(T_KEYS.STAY_HERE)}
            </Button>
          </Box>

          <Typography
            sx={{
              fontFamily: '"IBM Plex Sans Arabic", sans-serif',
              fontWeight: 400,
              fontSize: '12px',
              lineHeight: '16px',
              color: '#9A9A9A',
              textAlign: 'center',
            }}
          >
            {t(T_KEYS.IMPORTING_MODAL_NOTIFICATION)}
          </Typography>
        </Box>
      </DialogContent>
    </Dialog>
  );
});

ImportingModal.displayName = 'ImportingModal';

export default ImportingModal;
